import { useCallback, useEffect, useRef, useState } from 'react';
import { suggestName } from '../services/AiService';

const MIN_LENGTH = 3;

/**
 * Suggestion de reformulation d'intitulé déclenchée MANUELLEMENT (au clic).
 * Aucune analyse automatique : l'utilisateur clique sur l'icône IA du champ.
 *
 * Statuts :
 *  - 'idle'      : rien à afficher
 *  - 'short'     : intitulé trop court pour être analysé
 *  - 'proposal'  : une reformulation est proposée (voir `proposal`)
 *  - 'pertinent' : l'intitulé est déjà clair, aucune reformulation
 *  - 'error'     : l'IA est indisponible
 *
 * @param {'tache'|'affaire'} type
 */
export function useAiNameAssist(type) {
    const [loading, setLoading]   = useState(false);
    const [proposal, setProposal] = useState(null);
    const [status, setStatus]     = useState('idle');
    const abortRef = useRef(null);

    const reset = useCallback(() => {
        abortRef.current?.abort();
        setLoading(false);
        setProposal(null);
        setStatus('idle');
    }, []);

    const run = useCallback(async (value) => {
        const v = (value ?? '').trim();

        abortRef.current?.abort();

        if (v.length < MIN_LENGTH) {
            setProposal(null);
            setStatus('short');
            setLoading(false);
            return;
        }

        const controller = new AbortController();
        abortRef.current = controller;
        setLoading(true);
        setProposal(null);
        setStatus('idle');

        try {
            const res  = await suggestName(type, v, controller.signal);
            const text = res?.suggestion?.trim();
            if (text && text.toLowerCase() !== v.toLowerCase()) {
                setProposal(text);
                setStatus('proposal');
            } else {
                setStatus('pertinent');
            }
        } catch (err) {
            if (err?.name === 'CanceledError' || err?.name === 'AbortError') return;
            setStatus('error');
        } finally {
            setLoading(false);
        }
    }, [type]);

    // La confirmation « intitulé déjà clair » s'efface d'elle-même après quelques secondes.
    useEffect(() => {
        if (status !== 'pertinent') return undefined;
        const timer = setTimeout(() => setStatus('idle'), 4000);
        return () => clearTimeout(timer);
    }, [status]);

    useEffect(() => () => abortRef.current?.abort(), []);

    return { loading, proposal, status, run, reset };
}
